import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.bubble.css';
import { Button } from '../../components/ui/button';
import Input from '../../components/ui/input';
import InputEn from '../../components/InputEn';
import useCompanyProfile from '../../stores/useCompanyProfiles';
import { ICompanyProfile } from '../../types/types';

const sections: { id: SectionId; label: string }[] = [
  { id: 'story', label: 'Story' },
  { id: 'vision', label: 'Vision' },
  { id: 'mission', label: 'Mission' },
];

type SectionId = 'story' | 'vision' | 'mission';

function InfoCompany() {
  const { profile, fetchProfile, editProfile, loading } = useCompanyProfile();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    formState: { isDirty },
  } = useForm({
    defaultValues: profile || {
      story_id: 'Memuat...',
      story_en: 'Memuat...',
      vision_id: 'Memuat...',
      vision_en: 'Memuat...',
      mission_id: 'Memuat...',
      mission_en: 'Memuat...',
      phone_pr: 'Memuat...',
      operational: 'Memuat...',
      whatsapp_pr: 'Memuat...',
      email_pr: 'Memuat...',
      location_pr: 'Memuat...',
      map_url: 'Memuat...',
      html_map: 'Memuat...',
      ig_pr: 'Memuat...',
      tiktok_pr: 'Memuat...',
      fb_pr: 'Memuat...',
      x_pr: 'Memuat...',
    },
  });

  useEffect(() => {
    const loadProfile = async () => {
      const { success, data } = await fetchProfile();
      if (success && data) {
        reset(data);
      }
    };
    loadProfile();
  }, [fetchProfile, reset]);

  const onSubmit = async (formData: ICompanyProfile) => {
    if (!profile) return;
    await editProfile(formData);
    if (!loading) {
      const loadProfile = async () => {
        const { success, data } = await fetchProfile();
        if (success && data) {
          reset(data);
        }
      };
      loadProfile();
    }
    console.log(formData);
  };

  const handleCancel = () => {
    reset();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <section>
        <h4 className="font-medium text-md">Profile</h4>
        <div className="xl:flex gap-4">
          {sections.map(({ id, label }) => (
            <div key={id} className="flex flex-col w-full text-sm mt-2 gap-2">
              <p className="flex gap-1">
                {label} <span className="text-red-600">*</span>
              </p>

              <InputEn
                nama={`${id}_id`}
                namaEn={`${id}_en`}
                placeholder={`Input ${label.toLowerCase()} dari perusahaan...`}
                type="textarea"
                register={register}
                setValue={setValue}
                watch={watch}
              />
            </div>
          ))}
        </div>
      </section>

      <section className="mt-4">
        <h4 className="font-medium text-md">Contacts</h4>
        <div className="md:flex-row flex-col gap-4 flex">
          <div className="w-full">
            <div className="flex flex-col text-sm mt-2 gap-2">
              <p className="flex gap-1">
                Phone <span className="text-red-600">*</span>
              </p>
              <Input {...register('phone_pr')} type="tel" name="phone_pr" placeholder="Masukkan nomor telpon..." />
            </div>
            <div className="flex flex-col text-sm mt-2 gap-2">
              <p className="flex gap-1">
                Whatsapp <span className="text-red-600">*</span>
              </p>
              <span className="flex gap-3 items-center">
                +62 <Input {...register('whatsapp_pr')} type="tel" name="whatsapp_pr" placeholder="82xxx..." />
              </span>
            </div>
            <div className="flex flex-col text-sm mt-2 gap-2">
              <p className="flex gap-1">
                Email <span className="text-red-600">*</span>
              </p>
              <Input {...register('email_pr')} type="email" name="email_pr" placeholder="Masukkan email..." />
            </div>
            <div className="flex flex-col text-sm mt-2 gap-2">
              <p className="flex gap-1">
                Location <span className="text-red-600">*</span>
              </p>
              <Input
                {...register('location_pr')}
                type="text"
                name="location_pr"
                placeholder="Masukkan lokasi saat ini..."
              />
            </div>
            <div className="flex flex-col text-sm mt-2 gap-2">
              <p className="flex gap-1">
                Jam Operasional <span className="text-red-600">*</span>
              </p>
              <InputEn
                nama="operational"
                namaEn="operational_en"
                type="texteditor_isDirty"
                control={control}
                register={register}
                setValue={setValue}
                watch={watch}
              />
            </div>
            <div className="flex flex-col text-sm mt-2 gap-2">
              <p className="flex gap-1">
                Link Map <span className="text-red-600">*</span>
              </p>
              <Input {...register('map_url')} type="text" name="map_url" placeholder="Masukkan url map..." />
            </div>
            <div className="flex flex-col text-sm mt-2 gap-2">
              <p className="flex gap-1">
                Html Map <span className="text-red-600">*</span>
              </p>
              <Input {...register('html_map')} type="text" name="html_map" placeholder="Masukkan tag html map..." />
            </div>
          </div>
          <div className="w-full text-sm flex flex-col gap-3">
            <h3 className="text-sm">Cara mendapatkan HTML Map</h3>
            <div className="flex gap-3 items-center">
              <span className="px-3.5 py-1.5 bg-text text-sm text-neutral-50 rounded-full">1</span>
              <p>Pergi dan pilih lokasi di google map.</p>
            </div>
            <div className="flex gap-3 items-center">
              <span className="px-3 py-1.5 bg-text text-sm text-neutral-50 rounded-full">2</span>
              <p>Tekan tombol bagikan.</p>
            </div>
            <div className="flex gap-3 items-center">
              <span className="px-3 py-1.5 bg-text text-sm text-neutral-50 rounded-full">3</span>
              <p>Pilih menu sematkan peta/embed a map, lalu salin HTML.</p>
            </div>
            <div className="flex gap-3 items-center">
              <img src="https://images2.imgbox.com/b3/18/ZiyWn4hk_o.png" alt="" />
            </div>
          </div>
        </div>
      </section>

      <section className="mt-4">
        <h4 className="font-medium text-md">Platforms</h4>
        <div className="w-full lg:flex-row flex-col flex gap-4">
          <div className="flex flex-col text-sm w-full mt-2 gap-2">
            <p className="flex gap-1">
              Instagram <span className="text-red-600">*</span>
            </p>
            <Input {...register('ig_pr')} type="text" name="ig_pr" placeholder="Masukkan url instagram..." />
          </div>
          <div className="flex flex-col text-sm w-full mt-2 gap-2">
            <p className="flex gap-1">
              Tiktok <span className="text-red-600">*</span>
            </p>
            <Input {...register('tiktok_pr')} type="text" name="tiktok_pr" placeholder="Masukkan url tiktok..." />
          </div>
          <div className="flex flex-col text-sm w-full mt-2 gap-2">
            <p className="flex gap-1">
              Facebook <span className="text-red-600">*</span>
            </p>
            <Input {...register('fb_pr')} type="text" name="fb_pr" placeholder="Masukkan url facebook..." />
          </div>
          <div className="flex flex-col text-sm w-full mt-2 gap-2">
            <p className="flex gap-1">
              X/Twitter <span className="text-red-600">*</span>
            </p>
            <Input {...register('x_pr')} type="text" name="x_pr" placeholder="Masukkan url X/Twitter.." />
          </div>
        </div>
      </section>

      {isDirty && (
        <div className="mt-24">
          <div className="fixed bottom-0 right-0 md:bottom-4 md:right-1 bg-neutral-50/20 border backdrop-blur-md md:rounded-lg border-border flex items-center justify-between px-4 w-full md:w-2/3 z-50 py-2 transition-all animate-in animate-fade-in duration-300">
            <h3>Anda ingin menyimpan perubahan?</h3>
            <div className="flex gap-1">
              <Button className="mt-2" type="button" variant="outline" onClick={handleCancel}>
                Batal
              </Button>
              <Button className="mt-2" disabled={loading} type="submit" variant="default">
                {loading ? 'Menyimpan...' : 'Simpan'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </form>
  );
}

export default InfoCompany;
